// Multi-touch attribution — HubSpot pattern
// Tracks first/last touch and all touches across sessions
import { useEffect, useCallback } from "react";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "djayma-attribution";

interface TouchPoint {
  readonly source: string;
  readonly medium: string;
  readonly campaign: string;
  readonly content: string;
  readonly term: string;
  readonly referrer: string;
  readonly page_url: string;
  readonly timestamp: string;
}

interface AttributionData {
  readonly first_touch: TouchPoint | null;
  readonly last_touch: TouchPoint | null;
  readonly touches: readonly TouchPoint[];
}

function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  return {
    source: params.get("utm_source") ?? "",
    medium: params.get("utm_medium") ?? "",
    campaign: params.get("utm_campaign") ?? "",
    content: params.get("utm_content") ?? "",
    term: params.get("utm_term") ?? "",
  };
}

function loadAttribution(): AttributionData {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as AttributionData;
  } catch { /* fresh */ }
  return { first_touch: null, last_touch: null, touches: [] };
}

function saveAttribution(data: AttributionData): void {
  try {
    // Keep max 20 touches
    const trimmed = {
      ...data,
      touches: data.touches.slice(-20),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch { /* storage full */ }
}

export function useAttribution() {
  useEffect(() => {
    const utm = getUtmParams();
    const hasUtm = utm.source || utm.medium || utm.campaign;
    const hasReferrer = document.referrer && !document.referrer.includes(window.location.hostname);

    if (!hasUtm && !hasReferrer) return;

    const touch: TouchPoint = {
      source: utm.source ?? "",
      medium: utm.medium ?? "",
      campaign: utm.campaign ?? "",
      content: utm.content ?? "",
      term: utm.term ?? "",
      referrer: document.referrer || "",
      page_url: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    const current = loadAttribution();
    const updated: AttributionData = {
      first_touch: current.first_touch ?? touch,
      last_touch: touch,
      touches: [...current.touches, touch],
    };

    saveAttribution(updated);

    trackEvent("attribution_touch", {
      source: touch.source,
      medium: touch.medium,
      campaign: touch.campaign,
      referrer: touch.referrer,
      is_first_touch: current.first_touch === null,
      total_touches: updated.touches.length,
    });
  }, []);

  const getAttribution = useCallback((): AttributionData => loadAttribution(), []);

  return { getAttribution };
}
